import React, { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Modal from 'react-bootstrap/Modal';
import { confirmAlert } from 'react-confirm-alert';

import API from '@components/api';
import BasicNavMenu from '@components/Navbars/Basic';
import imgAlert from '@img/Homepage/Alert.png';
import { cleanString } from '@modules/_misc';
import { withinDateTimeRange } from '@modules/date';
import { formatNotificationBody } from '@modules/react_content';
import { globalActions } from '@reducers/global';
import { userActions } from '@reducers/user';
import PracticeWarningModal from '@views/Public/Modals/PracticeWarning';
import MobileSidebar from '@views/Public/shared-content/mobile-sidebar';
import withRouter from '@wrappers/withRouter';

const { VITE_ENV } = import.meta.env;

const SignIn = props => {
	const { navigate } = props,
		dispatch = useDispatch(),
		email = useSelector(state => state.user.email),
		support_student_id = useSelector(state => state.user.support_student_id),
		campus_admin = useSelector(state => state.user.campus_admin),
		direct_admit = useSelector(state => state.user.direct_admit),
		system_notifications = useSelector(state => state.global.system_notifications),
		login = (obj, direct_admit) => dispatch(userActions.login({ obj, direct_admit })),
		setForcePwdReset = () => dispatch(userActions.setForcePwdReset()),
		captureError = ex => dispatch(globalActions.captureError(ex)),
		[practiceModal, setPracticeModal] = useState(VITE_ENV === 'practice'),
		[userName, setUserName] = useState(''),
		[pwd, setPwd] = useState(''),
		[error, setError] = useState(''),
		[notificationIndex, setNotificationIndex] = useState(0),
		[intervalId, setIntervalId] = useState(-1),
		notifications = useMemo(() => filterNotifications(system_notifications), [system_notifications]),
		api = useMemo(() => new API('', support_student_id, campus_admin), [support_student_id, campus_admin]);

	useEffect(() => {
		if (notifications.length > 1) {
			let funcSetInterval = setInterval(() => {
				const new_index = notificationIndex === notifications.length - 1 ? 0 : notificationIndex + 1;
				setNotificationIndex(new_index);
			}, 5000);

			setIntervalId(funcSetInterval);
		}
	}, []);

	useEffect(() => {
		if (email) {
			if (pwd.length < 12) {
				setForcePwdReset();
			} else {
				const need_to_link_direct_admit = direct_admit?.need_to_link && !direct_admit.user_id;
				navigate(need_to_link_direct_admit ? '/link-direct-admit' : '/my-account');
			}
		}
	}, [email]);

	function onSignIn(e) {
		e.preventDefault();
		e.stopPropagation();

		api.login(userName, pwd)
			.then(result => {
				const strError = result.result === 'success' && result.user ? '' : result.reason;
				setError(strError);

				if (!strError) login(result.user, result.direct_admit_row || {});
			})
			.catch(ex => captureError(ex));
	}

	function onChange(e) {
		try {
			let { name, value } = e.target,
				label = name === 'userName' ? 'Email' : 'Password',
				valClean = cleanString(value);

			value = valClean.clean;
			if (valClean.dirty) {
				confirmAlert({
					customUI: ({ onClose }) => {
						return (
							<div className='react-confirm-alert'>
								<div className='react-confirm-alert-body'>
									Invalid characters were removed from "{label}."
									<div className='react-confirm-alert-button-group'>
										<button onClick={onClose}>OK</button>
									</div>
								</div>
							</div>
						);
					}
				});
			}

			if (name === 'userName') {
				setUserName(value);
			} else {
				setPwd(value);
			}

			setError('');
		} catch (ex) {
			captureError(ex);
		}
	}

	function renderNotifications() {
		const content = formatNotificationBody(notifications[notificationIndex]);

		return (
			<div className='announcement-modal'>
				<img src={imgAlert} alt='' className='announcement-icon' />
				<div>{content}</div>
			</div>
		);
	}

	function renderMainForm() {
		const err_messages = {
			'Bad Username': (
				<h4 className='text uwRedText'>
					Sorry, we could not find an account with this email. Please make sure your information is entered
					correctly and try again.
				</h4>
			),
			'Bad Password': (
				<h4 className='text uwRedText'>
					Sorry, the password you have entered is incorrect. Please make sure your information is entered
					correctly and try again.
				</h4>
			),
			'User Blocked': (
				<>
					<h4 className='text uwRedText'>The account associated with this email has been deactivated</h4>
					<h4 className='text uwRedText'>
						Please <a onClick={() => navigate('/create-account')}>create a new account</a> using a different
						email address.
					</h4>
				</>
			)
		};

		return (
			<div className='right-column-wrap'>
				<div className='form-block w-form'>
					<h1 className='column-heading'>sign in to your account</h1>

					<form id='email-form' name='email-form' data-name='Email Form' className='form' onSubmit={onSignIn}>
						<label htmlFor='username' className='field-label'>
							Email
						</label>
						<input
							type='text'
							className='text-field w-input'
							maxLength='256'
							name='userName'
							data-name='Email'
							placeholder='Enter your email'
							value={userName}
							required={true}
							onChange={onChange}
							id='username'
						/>

						<label htmlFor='Password' className='field-label'>
							Password
						</label>
						<input
							aria-label='Password input'
							type='password'
							className='text-field w-input'
							maxLength='256'
							name='pwd'
							data-name='Password'
							placeholder='Enter your password'
							value={pwd}
							required={true}
							minLength={8}
							onChange={onChange}
							id='password'
						/>

						{!!error && err_messages[error]}

						<input
							aria-label='Sign in submit'
							type='submit'
							value='Sign In'
							data-wait='Please wait...'
							className='submit-button w-button'
						/>
					</form>
				</div>
				<a className='link' onClick={() => navigate('/reset-password')}>
					Forgot My Password
				</a>
			</div>
		);
	}

	return (
		<div className='body-2' role='main'>
			<Modal show={practiceModal}>
				<PracticeWarningModal closeModal={() => setPracticeModal(false)} />
			</Modal>

			<MobileSidebar />

			<div className='two-column-view' role='navigation'>
				<BasicNavMenu />

				<div className='right-column'>
					{!!notifications.length && renderNotifications()}

					{renderMainForm()}
				</div>
			</div>
			<script
				src='https://d3e54v103j8qbb.cloudfront.net/js/jquery-3.4.1.min.220afd743d.js?site=5ea1ccd99c38e78f25db182e'
				type='text/javascript'
				integrity='sha256-CSXorXvZcTkaix6Yvo6HppcZGetbYMGWSFlBw8HfCJo='
				crossOrigin='anonymous'></script>
			<script src='@webflowJS/webflow.js' type='text/javascript'></script>
		</div>
	);
};

const filterNotifications = arr =>
	arr.filter(n => {
		const { field_start_date: start, field_end_date: end, field_display_on_login_screen: on_login } = n || {};
		return !start || !end || (on_login === '1' && withinDateTimeRange(start, end));
	});

export default withRouter(SignIn);
